import React from 'react'

import MoreHorizontalIcon from '@SonarCX/components/icons/MoreHorizontalIcon'

import ButtonMenu from '@SonarCX/components/molecules/ButtonMenu2'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useFavorites from '@SonarCX/hooks/useFavorites'
import useStyles from './styles'

const START_EDIT = 'Edit'
const SAVE_EDIT = 'Save'
const SAVE_DRAFT = 'Save as draft'
const CANCEL_EDIT = 'Cancel'
const PUBLISH = 'Publish'
const UNPUBLISH = 'Unpublish'
const FAVORITE = 'Favorite'
const UNFAVORITE = 'Unfavorite'
const DELETE_DOCUMENT = 'Delete Article'

type ArticleMobileOptionsProps = {
  editing: boolean
  published: boolean
  isFavorite: boolean
  articleID: string
  handleCancelEdit: () => void
  handleSaveClick: () => void
  handleChangePublish: (shouldPublish: boolean) => void
  handleDelete: () => void
}

type OptType = {
  name: string
  Icon?: (props: any) => JSX.Element
}

const ArticleMobileOptions: React.FC<ArticleMobileOptionsProps> = ({
  editing,
  published,
  isFavorite,
  articleID,
  handleCancelEdit,
  handleSaveClick,
  handleChangePublish,
  handleDelete
}) => {
  const hasPermission = useAccess()
  const { user } = useEnvironment()
  const { handleFavoriteClick } = useFavorites()

  const canModify = React.useMemo(
    () => hasPermission([PERMS.ArticlesFull, PERMS.ArticlesAdmin]),
    [hasPermission]
  )

  const options = React.useMemo(() => {
    const opts: OptType[] = []

    if (!editing) {
      opts.push({ name: START_EDIT })
    } else {
      if (published) {
        opts.push({ name: SAVE_EDIT })
      } else {
        opts.push({ name: SAVE_DRAFT })
      }

      opts.push({ name: CANCEL_EDIT })
    }

    if (!published) {
      opts.push({ name: PUBLISH })
    } else {
      opts.push({ name: UNPUBLISH })
    }

    if (!isFavorite) {
      opts.push({ name: FAVORITE })
    } else {
      opts.push({ name: UNFAVORITE })
    }

    if (canModify) {
      opts.push({
        name: DELETE_DOCUMENT
      })
    }

    return opts
  }, [editing, published, isFavorite, canModify])

  const classes = useStyles()

  const handleSetFavorite = React.useCallback(
    (isFavorite: boolean) => {
      handleFavoriteClick(user.id, isFavorite, articleID, 'article')
    },
    [handleFavoriteClick, user, articleID]
  )

  const handleSelect = React.useCallback(
    (item: string) => {
      if ([START_EDIT, SAVE_EDIT, SAVE_DRAFT].includes(item)) {
        handleSaveClick()
        return
      }

      if (item === CANCEL_EDIT) {
        handleCancelEdit()
        return
      }

      if (item === PUBLISH) {
        handleChangePublish(true)
        return
      }

      if (item === UNPUBLISH) {
        handleChangePublish(false)
        return
      }

      if (item === FAVORITE) {
        handleSetFavorite(false)
        return
      }

      if (item === UNFAVORITE) {
        handleSetFavorite(true)
        return
      }

      if (item === DELETE_DOCUMENT) {
        handleDelete()
        return
      }
    },
    [
      handleSaveClick,
      handleCancelEdit,
      handleChangePublish,
      handleSetFavorite,
      handleDelete
    ]
  )

  return (
    <ButtonMenu
      options={options}
      onSelect={handleSelect}
      className={classes.root}
      // @ts-expect-error Prop types incorrect
      classes={{ title: classes.title }}
    >
      <MoreHorizontalIcon />
    </ButtonMenu>
  )
}

export default ArticleMobileOptions
